import React, { ComponentType } from 'react'
import { IGatsbyImageData } from 'gatsby-plugin-image'
import { Header } from '../Header/Header'
import BackgroundImage from '../Image/BackgroundImage'

export type BoxImageUrl = {
  url: string
  file?: {
    url: string
  }
}

export type BoxImage = BoxImageUrl | IGatsbyImageData

export type BoxHrefProps = {
  link: string
}

export type BoxLinkProps = {
  title: string
  link: BoxHrefProps
  target?: string
}
export type BoxButtonProps = {
  cta: string
  link: BoxHrefProps
  target?: string
}

export type BoxProps = {
  fullHeight?: boolean
  isPromoBox?: boolean
  button?: BoxButtonProps
  header: BoxLinkProps
  image?: BoxImage
  renderLink?: ComponentType<BoxRenderLinkProps>
  renderButton?: ComponentType<BoxRenderButtonProps>
}

export type BoxRenderLinkProps = BoxLinkProps
export type BoxRenderButtonProps = BoxButtonProps

const renderLinkDefault = ({ link, title, target }: BoxRenderLinkProps) => (
  <a aria-label={title} href={link.link} target={target || '_self'}>
    {title}
  </a>
)

const renderButtonDefault = (buttonClass: string) => {
  return ({ link, cta, target }: BoxRenderButtonProps) => {
    if (link && link.link && cta) {
      return (
        <a
          aria-label={cta}
          href={link.link}
          className={`frw-button frw-${buttonClass}`}
          target={target || '_self'}
        >
          {cta}
        </a>
      )
    }
    return null
  }
}

const boxClass = (
  fullHeight: boolean,
  isPromoBox: boolean,
  button?: BoxRenderButtonProps,
  image?: BoxImage
) => {
  if (fullHeight) {
    return 'frw-box frw-box--2'
  }
  if (isPromoBox) {
    return 'frw-box frw-box--1 frw-bg--img frw-bg--blend'
  }
  if (image) {
    return 'frw-box frw-box--3 frw-bg--img frw-bg--blend'
  }
  if (!fullHeight || !button) {
    return 'frw-box frw-box--1'
  }

  return ''
}

export const Box = ({
  fullHeight = false,
  isPromoBox = false,
  button,
  header,
  image,
  renderLink: Link = renderLinkDefault,
  renderButton: Button = renderButtonDefault(
    image ? 'button--6-4' : 'button--6-2'
  )
}: BoxProps) => {
  const containerClass = boxClass(fullHeight, isPromoBox, button, image)

  // content that goes inside the box
  const boxContent = (
    <>
      <Header
        level={4}
        className={fullHeight ? 'frw-heading--4' : 'frw-heading--5'}
      >
        <Link {...header} />
      </Header>
      {fullHeight && button && button.cta && (
        <div className="frw-button__wrapper frw-button__wrapper--1">
          <Button {...button} />
        </div>
      )}
    </>
  )
  // type guards to determine image type
  const hasGatsbyImageData =
    image && ('images' in image || 'gatsbyImageData' in image)
  const hasUrlOnly = image && ('file' in image || 'url' in image)

  // render based on image type
  if (hasGatsbyImageData) {
    return (
      <BackgroundImage
        gatsbyImageData={image as IGatsbyImageData}
        Tag="div"
        classname={containerClass}
      >
        {boxContent}
      </BackgroundImage>
    )
  }

  if (hasUrlOnly) {
    const imageUrl =
      (image as BoxImageUrl).file?.url || (image as BoxImageUrl).url
    return (
      <div
        className={containerClass}
        style={{
          ...(imageUrl
            ? {
                backgroundImage: `url(${imageUrl}), linear-gradient(108deg, #3e5151 0%,#decba4 100%)`,
                color: '#ffffff'
              }
            : {
                backgroundColor: '#171721'
              })
        }}
      >
        {boxContent}
      </div>
    )
  }

  // no image - show only box contents
  return <div className={containerClass}>{boxContent}</div>
}
export default Box
